'use client'

import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import Link from 'next/link'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

const WIDTH = 1600
const HEIGHT = 800
const BAR_TOP = 55
const BAR_BOTTOM = 610
const DEFAULT_DIGITS = '073854008089'
const DEFAULT_COLOUR = '#00052f'
const DEFAULT_PNG_DPI = 600
const PNG_RESOLUTIONS = [203, 300, 600] as const
const GIMMICKED_DECKS = [
  'BRAINWAVE',
  'FORCINGDECK',
  'INVISIBLEDECK',
  'MARKEDDECK',
  'MENETEKEL',
  'MENTALPHOTO',
  'MIRAGEDECK',
  'ONEWAYFORCE',
  'POPEYEDPOPPER',
  'RADIODECK',
  'RISINGCARDS',
  'ROUGHSMOOTH',
  'SHORTDECK',
  'STRIPPERDECK',
  'SVENGALIDECK',
] as const

const GLYPHS: Record<string, string[]> = {
  A: ['M0.08 1 L0.32 0 L0.68 0 L0.92 1', 'M0.19 0.58 L0.81 0.58'],
  B: [
    'M0.1 0 L0.1 1',
    'M0.1 0 L0.58 0 Q0.88 0 0.88 0.25 Q0.88 0.5 0.58 0.5 L0.1 0.5',
    'M0.58 0.5 Q0.92 0.5 0.92 0.75 Q0.92 1 0.58 1 L0.1 1',
  ],
  C: ['M0.9 0.12 Q0.72 0 0.48 0 Q0.1 0 0.1 0.5 Q0.1 1 0.48 1 Q0.72 1 0.9 0.88'],
  D: ['M0.1 0 L0.1 1', 'M0.1 0 L0.48 0 Q0.9 0 0.9 0.5 Q0.9 1 0.48 1 L0.1 1'],
  E: ['M0.88 0 L0.1 0 L0.1 1 L0.88 1', 'M0.1 0.5 L0.72 0.5'],
  F: ['M0.1 1 L0.1 0 L0.9 0', 'M0.1 0.48 L0.72 0.48'],
  G: [
    'M0.9 0.14 Q0.72 0 0.48 0 Q0.1 0 0.1 0.5 Q0.1 1 0.48 1 Q0.9 1 0.9 0.58 L0.54 0.58',
  ],
  H: ['M0.1 0 L0.1 1', 'M0.9 0 L0.9 1', 'M0.1 0.5 L0.9 0.5'],
  I: ['M0.18 0 L0.82 0', 'M0.5 0 L0.5 1', 'M0.18 1 L0.82 1'],
  J: ['M0.2 0 L0.9 0', 'M0.68 0 L0.68 0.76 Q0.68 1 0.36 1 Q0.08 1 0.08 0.72'],
  K: ['M0.1 0 L0.1 1', 'M0.9 0 L0.1 0.58', 'M0.36 0.45 L0.92 1'],
  L: ['M0.1 0 L0.1 1 L0.9 1'],
  M: ['M0.06 1 L0.06 0 L0.5 0.58 L0.94 0 L0.94 1'],
  N: ['M0.1 1 L0.1 0 L0.9 1 L0.9 0'],
  O: ['M0.5 0 Q0.1 0 0.1 0.5 Q0.1 1 0.5 1 Q0.9 1 0.9 0.5 Q0.9 0 0.5 0'],
  P: ['M0.1 1 L0.1 0 L0.55 0 Q0.9 0 0.9 0.3 Q0.9 0.58 0.55 0.58 L0.1 0.58'],
  Q: [
    'M0.5 0 Q0.1 0 0.1 0.5 Q0.1 1 0.5 1 Q0.9 1 0.9 0.5 Q0.9 0 0.5 0',
    'M0.58 0.7 L0.94 1',
  ],
  R: [
    'M0.1 1 L0.1 0 L0.55 0 Q0.9 0 0.9 0.3 Q0.9 0.56 0.55 0.56 L0.1 0.56',
    'M0.52 0.56 L0.92 1',
  ],
  S: [
    'M0.88 0.12 Q0.7 0 0.46 0 Q0.1 0 0.1 0.28 Q0.1 0.48 0.5 0.5 Q0.9 0.52 0.9 0.74 Q0.9 1 0.52 1 Q0.26 1 0.08 0.88',
  ],
  T: ['M0.04 0 L0.96 0', 'M0.5 0 L0.5 1'],
  U: ['M0.1 0 L0.1 0.72 Q0.1 1 0.5 1 Q0.9 1 0.9 0.72 L0.9 0'],
  V: ['M0.06 0 L0.4 1 L0.6 1 L0.94 0'],
  W: ['M0.04 0 L0.2 1 L0.5 0.48 L0.8 1 L0.96 0'],
  X: ['M0.08 0 L0.92 1', 'M0.92 0 L0.08 1'],
  Y: ['M0.06 0 L0.5 0.52 L0.94 0', 'M0.5 0.52 L0.5 1'],
  Z: ['M0.08 0 L0.92 0 L0.08 1 L0.92 1'],
}

type BarcodeBar = {
  x: number
  width: number
  guard: boolean
}

function cleanWord(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 16)
}

function cleanDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, 12)
}

function seededValue(seed: number) {
  const value = Math.sin(seed * 12.9898) * 43758.5453
  return value - Math.floor(value)
}

function scaleGlyphPath(
  path: string,
  x: number,
  y: number,
  width: number,
  height: number
) {
  let coordinateIndex = 0
  return path.replace(/-?\d*\.?\d+/g, value => {
    const coordinate = Number(value)
    const scaled =
      coordinateIndex % 2 === 0
        ? x + coordinate * width
        : y + coordinate * height
    coordinateIndex += 1
    return scaled.toFixed(2)
  })
}

function createBars(word: string, density: number): BarcodeBar[] {
  const bars: BarcodeBar[] = []
  const guards = [88, 108, 140, 1460, 1492, 1512]

  guards.forEach((x, index) => {
    bars.push({ x, width: index % 3 === 1 ? 12 : 18, guard: true })
  })

  const seed = [...word].reduce(
    (total, character, index) => total + character.charCodeAt(0) * (index + 3),
    17
  )
  const minimumGap = 20 - density / 7
  let x = 182
  let index = 0

  while (x < 1434) {
    const width = 4 + Math.floor(seededValue(seed + index) * 12)
    bars.push({ x: Math.round(x), width, guard: false })
    x += width + minimumGap + seededValue(seed + index + 100) * 15
    index += 1
  }

  return bars
}

function formatDigits(digits: string) {
  const padded = digits.padEnd(12, '0')
  return {
    left: padded.slice(0, 1),
    firstGroup: padded.slice(1, 6),
    secondGroup: padded.slice(6, 11),
    right: padded.slice(11, 12),
  }
}

function makeFilename(word: string, extension: 'svg' | 'png') {
  return `${word.toLowerCase() || 'hidden-word'}-barcode.${extension}`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0)
    }
  }
  return (crc ^ 0xffffffff) >>> 0
}

async function addPngDensityMetadata(blob: Blob, dpi: number) {
  const source = new Uint8Array(await blob.arrayBuffer())
  const pixelsPerMetre = Math.round(dpi / 0.0254)
  const chunk = new Uint8Array(21)
  const chunkView = new DataView(chunk.buffer)
  const typeAndData = new Uint8Array(13)

  chunkView.setUint32(0, 9)
  typeAndData.set([0x70, 0x48, 0x59, 0x73], 0)
  const metadataView = new DataView(typeAndData.buffer)
  metadataView.setUint32(4, pixelsPerMetre)
  metadataView.setUint32(8, pixelsPerMetre)
  typeAndData[12] = 1
  chunk.set(typeAndData, 4)
  chunkView.setUint32(17, crc32(typeAndData))

  let insertionPoint = 8
  while (insertionPoint < source.length) {
    const sourceView = new DataView(
      source.buffer,
      source.byteOffset + insertionPoint
    )
    const length = sourceView.getUint32(0)
    const type = String.fromCharCode(
      ...source.slice(insertionPoint + 4, insertionPoint + 8)
    )
    if (type === 'pHYs') {
      const output = source.slice()
      output.set(chunk, insertionPoint)
      return new Blob([output], { type: 'image/png' })
    }
    if (type === 'IDAT') break
    insertionPoint += length + 12
  }

  const output = new Uint8Array(source.length + chunk.length)
  output.set(source.slice(0, insertionPoint), 0)
  output.set(chunk, insertionPoint)
  output.set(source.slice(insertionPoint), insertionPoint + chunk.length)
  return new Blob([output], { type: 'image/png' })
}

export function HiddenBarcodeGeneratorClient() {
  const [word, setWord] = useState('')
  const [digits, setDigits] = useState(DEFAULT_DIGITS)
  const [colour, setColour] = useState(DEFAULT_COLOUR)
  const [density, setDensity] = useState(72)
  const [visibility, setVisibility] = useState(45)
  const [exportMessage, setExportMessage] = useState('')
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [pngDpi, setPngDpi] = useState<number>(DEFAULT_PNG_DPI)
  const svgRef = useRef<SVGSVGElement>(null)
  const clipId = useId().replaceAll(':', '')

  const bars = useMemo(() => createBars(word, density), [word, density])
  const numberGroups = formatDigits(digits)
  const characterWidth = Math.min(142, 1120 / Math.max(word.length, 1))
  const wordWidth = characterWidth * word.length
  const wordStart = (WIDTH - wordWidth) / 2
  const letterStroke = Math.max(20, characterWidth * 0.21)
  const separationStroke = letterStroke + 8 + (visibility - 20) * 0.2
  const camouflageInterval = visibility >= 70 ? 6 : visibility >= 45 ? 4 : 3

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * GIMMICKED_DECKS.length)
    queueMicrotask(() => setWord(GIMMICKED_DECKS[randomIndex]))
  }, [])

  const downloadSvg = () => {
    if (!svgRef.current) return
    const source = new XMLSerializer().serializeToString(svgRef.current)
    downloadBlob(
      new Blob([source], { type: 'image/svg+xml;charset=utf-8' }),
      makeFilename(word, 'svg')
    )
    setExportMessage('SVG downloaded')
  }

  const downloadPng = () => {
    if (!svgRef.current) return
    const exportSvg = svgRef.current.cloneNode(true) as SVGSVGElement
    exportSvg.setAttribute('width', String(pngDpi))
    exportSvg.setAttribute('height', String(Math.round(pngDpi / 2)))
    const source = new XMLSerializer().serializeToString(exportSvg)
    const sourceUrl = URL.createObjectURL(
      new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
    )
    const image = new Image()

    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = pngDpi
      canvas.height = Math.round(pngDpi / 2)
      const context = canvas.getContext('2d')
      if (!context) {
        URL.revokeObjectURL(sourceUrl)
        setExportMessage(
          'Unable to create the PNG. Please try the SVG instead.'
        )
        return
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(sourceUrl)
      canvas.toBlob(async blob => {
        if (!blob) {
          setExportMessage(
            'Unable to create the PNG. Please try the SVG instead.'
          )
          return
        }

        try {
          const printReadyBlob = await addPngDensityMetadata(blob, pngDpi)
          downloadBlob(printReadyBlob, makeFilename(word, 'png'))
          setExportMessage(
            `PNG downloaded at ${pngDpi} DPI (${pngDpi} × ${Math.round(pngDpi / 2)} px)`
          )
        } catch {
          setExportMessage(
            'Unable to prepare the PNG. Please try the SVG instead.'
          )
        }
      }, 'image/png')
    }

    image.onerror = () => {
      URL.revokeObjectURL(sourceUrl)
      setExportMessage('Unable to create the PNG. Please try the SVG instead.')
    }

    image.src = sourceUrl
  }

  return (
    <div className="w-full">
      <header className="mb-8 grid items-center gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Gimmicked Deck Barcode Creator
          </h1>
          <p className="mt-3 text-lg leading-7 text-gray-600">
            Andi Gladwin here. I am constantly frustrated when I can&apos;t find
            a specific gimmicked deck. Openly marking the card box seems like an
            easy way to create a telltale sign that the cards are in some way
            prepared. Therefore, I had an idea: hide the marking in the barcode.
            This generator allows you to create custom barcodes, then print them
            and glue them to the bottom of your box. I hope you find it useful.
          </p>
        </div>

        <img
          src="https://vinc.gumlet.io/pics/barcode-generator.jpg?w=840&dpr=2"
          alt="A hidden-word barcode label attached to a gimmicked deck card box"
          width="1680"
          height="711"
          className="h-auto w-full rounded-2xl object-cover"
        />
      </header>

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <section className="rounded-2xl border border-gray-200 bg-white p-5">
          <h2 className="text-lg font-bold">Create your own</h2>

          <div className="mt-5 space-y-5">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">
                Hidden word
              </span>
              <input
                value={word}
                onChange={event => setWord(cleanWord(event.target.value))}
                className="w-full rounded-lg border-gray-300 uppercase focus:border-vi-red focus:ring-vi-red"
                maxLength={16}
                placeholder="TRIUMPHANT"
                list="gimmicked-deck-suggestions"
              />
              <datalist id="gimmicked-deck-suggestions">
                {GIMMICKED_DECKS.map(deck => (
                  <option key={deck} value={deck} />
                ))}
              </datalist>
              <span className="mt-1 block text-xs text-gray-500">
                A–Z only, up to 16 letters
              </span>
            </label>

            <button
              type="button"
              onClick={() => setShowAdvancedOptions(current => !current)}
              aria-expanded={showAdvancedOptions}
              aria-controls="advanced-barcode-options"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-vi-red underline-offset-4 hover:underline"
            >
              Advanced options
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            <div
              id="advanced-barcode-options"
              hidden={!showAdvancedOptions}
              className="space-y-5 border-t border-gray-200 pt-5"
            >
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold">
                  Barcode numbers
                </span>
                <input
                  value={digits}
                  onChange={event => setDigits(cleanDigits(event.target.value))}
                  inputMode="numeric"
                  className="w-full rounded-lg border-gray-300 font-mono tracking-wider focus:border-vi-red focus:ring-vi-red"
                  maxLength={12}
                  aria-describedby="barcode-number-help"
                />
                <span
                  id="barcode-number-help"
                  className="mt-1 block text-xs text-gray-500"
                >
                  12 digits; artwork only, not guaranteed to scan
                </span>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold">
                  PNG print resolution
                </span>
                <select
                  value={pngDpi}
                  onChange={event => setPngDpi(Number(event.target.value))}
                  className="w-full rounded-lg border-gray-300 focus:border-vi-red focus:ring-vi-red"
                >
                  {PNG_RESOLUTIONS.map(resolution => (
                    <option key={resolution} value={resolution}>
                      {resolution} DPI ({resolution} ×{' '}
                      {Math.round(resolution / 2)} px)
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center justify-between text-sm font-semibold">
                  Bar density{' '}
                  <span className="font-normal text-gray-500">{density}%</span>
                </span>
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={density}
                  onChange={event => setDensity(Number(event.target.value))}
                  className="w-full accent-[#00052f]"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center justify-between text-sm font-semibold">
                  Word visibility{' '}
                  <span className="font-normal text-gray-500">
                    {visibility}%
                  </span>
                </span>
                <input
                  type="range"
                  min="20"
                  max="90"
                  value={visibility}
                  onChange={event => setVisibility(Number(event.target.value))}
                  className="w-full accent-[#00052f]"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold">
                  Colour
                </span>
                <span className="flex gap-3">
                  <input
                    type="color"
                    value={colour}
                    onChange={event => setColour(event.target.value)}
                    className="h-11 w-14 cursor-pointer rounded border border-gray-300 bg-white p-1"
                    aria-label="Artwork colour"
                  />
                  <input
                    value={colour}
                    onChange={event => {
                      if (/^#[0-9a-fA-F]{0,6}$/.test(event.target.value)) {
                        setColour(event.target.value)
                      }
                    }}
                    onBlur={() => {
                      if (!/^#[0-9a-fA-F]{6}$/.test(colour))
                        setColour(DEFAULT_COLOUR)
                    }}
                    className="min-w-0 flex-1 rounded-lg border-gray-300 font-mono uppercase focus:border-vi-red focus:ring-vi-red"
                    aria-label="Artwork colour hex value"
                  />
                </span>
              </label>
            </div>
          </div>
        </section>

        <section className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              width="1in"
              height="0.5in"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label={`Barcode artwork containing the word ${word || 'blank'}`}
              className="block h-auto w-full"
            >
              <title>{`${word || 'Hidden word'} barcode artwork`}</title>
              <rect width={WIDTH} height={HEIGHT} fill="#ffffff" />
              <defs>
                <clipPath id={clipId}>
                  <rect x="0" y="0" width={WIDTH} height={HEIGHT} />
                </clipPath>
                <clipPath id={`${clipId}-letters`}>
                  <rect
                    x="0"
                    y={BAR_TOP}
                    width={WIDTH}
                    height={BAR_BOTTOM - BAR_TOP}
                  />
                </clipPath>
              </defs>

              <g fill={colour} clipPath={`url(#${clipId})`}>
                {bars.map((bar, index) => (
                  <rect
                    key={`${bar.x}-${index}`}
                    x={bar.x}
                    y={BAR_TOP}
                    width={bar.width}
                    height={
                      bar.guard
                        ? BAR_BOTTOM - BAR_TOP + 55
                        : BAR_BOTTOM - BAR_TOP
                    }
                  />
                ))}
              </g>

              <g
                fill="none"
                strokeLinecap="butt"
                strokeLinejoin="round"
                clipPath={`url(#${clipId}-letters)`}
                aria-hidden="true"
              >
                {[...word].map((character, index) => {
                  const x = wordStart + index * characterWidth
                  const paths = GLYPHS[character] ?? []
                  return (
                    <g key={`${character}-${index}`}>
                      {paths.map((path, pathIndex) => (
                        <path
                          key={pathIndex}
                          d={scaleGlyphPath(
                            path,
                            x + characterWidth * 0.12,
                            BAR_TOP,
                            characterWidth * 0.76,
                            BAR_BOTTOM - BAR_TOP
                          )}
                          stroke="#ffffff"
                          strokeWidth={separationStroke}
                        />
                      ))}
                      {paths.map((path, pathIndex) => (
                        <path
                          key={`ink-${pathIndex}`}
                          d={scaleGlyphPath(
                            path,
                            x + characterWidth * 0.12,
                            BAR_TOP,
                            characterWidth * 0.76,
                            BAR_BOTTOM - BAR_TOP
                          )}
                          stroke={colour}
                          strokeWidth={letterStroke}
                        />
                      ))}
                    </g>
                  )
                })}
              </g>

              <g fill={colour} aria-hidden="true">
                {bars.map((bar, index) =>
                  !bar.guard && index % camouflageInterval === 0 ? (
                    <rect
                      key={`camouflage-${bar.x}-${index}`}
                      x={bar.x + Math.max(0, (bar.width - 3) / 2)}
                      y={BAR_TOP}
                      width={Math.min(3, bar.width)}
                      height={BAR_BOTTOM - BAR_TOP}
                    />
                  ) : null
                )}
              </g>

              <g
                fill={colour}
                fontFamily="Arial, Helvetica, sans-serif"
                fontSize="94"
                fontWeight="500"
                textAnchor="middle"
              >
                <text x="55" y="755">
                  {numberGroups.left}
                </text>
                <text x="520" y="755" letterSpacing="22">
                  {numberGroups.firstGroup}
                </text>
                <text x="1080" y="755" letterSpacing="22">
                  {numberGroups.secondGroup}
                </text>
                <text x="1545" y="755">
                  {numberGroups.right}
                </text>
              </g>
            </svg>
          </div>

          {!word && (
            <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Enter a word to add it to the barcode.
            </p>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={downloadSvg}
              disabled={!word || digits.length !== 12}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#00052f] px-5 py-2.5 font-semibold text-[#00052f] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
              Download SVG
            </button>
            <button
              type="button"
              onClick={downloadPng}
              disabled={!word || digits.length !== 12}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#00052f] px-5 py-2.5 font-semibold text-white transition hover:bg-[#151a43] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
              Download PNG ({pngDpi} DPI)
            </button>
          </div>
          <p
            className="mt-3 min-h-5 text-right text-sm font-medium text-green-700"
            role="status"
            aria-live="polite"
          >
            {exportMessage}
          </p>
        </section>
      </div>

      <section className="mt-8 rounded-2xl bg-[#f2f2f2] p-6 sm:p-8">
        <h2 className="text-2xl font-bold">Learn more about gimmicked decks</h2>
        <p className="mt-3 leading-7 text-gray-600">
          See how the{' '}
          <Link
            href="/info/invisible-deck/"
            className="font-semibold text-vi-red underline underline-offset-4"
          >
            Invisible Deck
          </Link>{' '}
          and{' '}
          <Link
            href="/info/svengali-deck/"
            className="font-semibold text-vi-red underline underline-offset-4"
          >
            Svengali Deck
          </Link>{' '}
          work, explore our guide to{' '}
          <Link
            href="/info/marked-cards/"
            className="font-semibold text-vi-red underline underline-offset-4"
          >
            marked cards
          </Link>
          , or browse our range of{' '}
          <Link
            href="/playing-cards/"
            className="font-semibold text-vi-red underline underline-offset-4"
          >
            playing cards
          </Link>
          . If you&apos;re learning a memorized deck, practise with our free{' '}
          <Link
            href="/stack-trainer/"
            className="font-semibold text-vi-red underline underline-offset-4"
          >
            Stack Trainer
          </Link>
          .
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">Two quick tips</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 leading-7 text-gray-600">
          <li>Print the barcodes on self-adhesive label paper.</li>
          <li>
            Put the cellophane over the label to help obscure the hidden word.
          </li>
        </ul>
      </section>

      <footer className="mt-10 border-t border-gray-200 pt-8">
        <p className="leading-7 text-gray-600">
          The bars are decorative so the hidden word can remain readable. They
          won&apos;t scan or operate as a real barcode. When you download them,
          you can print them at 1″ × 0.5″ and they will be the perfect size for
          a standard USPCC card box.
        </p>
      </footer>
    </div>
  )
}
